import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { TrendingUp, Shield, Target, BarChart3 } from "lucide-react";
import { SocialShare } from "@/components/SocialShare";

const PRange = () => {
  return (
    <div className="py-8">
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-4 bg-primary-foreground text-primary">Premium Range</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">P Range Portfolios</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto mb-4">
              Premium Product for a Premium Investor
            </p>
            <div className="flex justify-center items-center gap-8 text-lg mb-6">
              <div>
                <span className="font-semibold">AMC:</span> 0.35% p.a.
              </div>
              <div>
                <span className="font-semibold">Rebalancing:</span> Quarterly
              </div>
            </div>
            <div className="flex justify-center">
              <SocialShare title="CBAM P Range Portfolios" variant="light" />
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Selection */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Choose Your P Range Portfolio</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Five portfolios designed to match different risk profiles and investment objectives, from defensive to aggressive growth strategies.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Badge className="w-fit mb-2 bg-green-100 text-green-800">Target Audience: Defensive</Badge>
                <CardTitle>P1 - Defensive</CardTitle>
                <CardDescription>Capital preservation focus with minimal risk</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/portfolios/p1">Learn More</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Badge className="w-fit mb-2 bg-blue-100 text-blue-800">Target Audience: Cautious</Badge>
                <CardTitle>P2 - Cautious</CardTitle>
                <CardDescription>Conservative growth with controlled risk</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/portfolios/p2">Learn More</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Badge className="w-fit mb-2 bg-yellow-100 text-yellow-800">Target Audience: Balanced</Badge>
                <CardTitle>P3 - Balanced</CardTitle>
                <CardDescription>Equal weighting of growth and income assets</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/portfolios/p3">Learn More</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Badge className="w-fit mb-2 bg-orange-100 text-orange-800">Target Audience: Capital Growth</Badge>
                <CardTitle>P4 - Capital Growth</CardTitle>
                <CardDescription>Growth-focused with higher equity allocation</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/portfolios/p4">Learn More</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow lg:col-start-2">
              <CardHeader>
                <Badge className="w-fit mb-2 bg-red-100 text-red-800">Target Audience: Aggressive</Badge>
                <CardTitle>P5 - Aggressive</CardTitle>
                <CardDescription>Maximum growth potential for risk-tolerant investors</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/portfolios/p5">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">P Range Overview</h2>
              <p className="text-lg text-muted-foreground mb-6">
                The use of active fund managers can allow Cape Berkshire to achieve a performance different to their
                underlying benchmark constituents, as active fund managers seek to outperform an index.
              </p>
              <p className="text-muted-foreground mb-6">
                We spend a very considerable amount of effort analysing active fund managers to ensure that they match our
                targeted aims and that they provide value for money for our clients by achieving their stated aims in outperforming their
                benchmarks.
              </p>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Key Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Active fund management
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Full investment universe
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Unconstrained allocation
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    12+ years experience
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment Approach */}
      <section className="py-16 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Investment Approach</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Active Management</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Utilisation of active managers where we see value, with passive alternatives where active management
                  provides little benefit over passive/index equivalents.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Full Investment Universe</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Open to utilisation of OEICs, ETFs, and Investment Trusts to gain access to wider investment
                  opportunities and reduce liquidity risk in alternative assets.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Unconstrained Allocation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Where we see exceptional value, we can allocate considerably more than benchmark. Similarly, where we see
                  overvaluation, we can underweight by large amounts.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Card className="text-center">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to Explore P Range Portfolios?</h3>
              <p className="text-muted-foreground mb-6">
                Discover how our premium portfolios can help you achieve your investment objectives with active management and full investment universe access.
              </p>
              <Button size="lg" asChild>
                <Link to="/contact">Schedule a Consultation</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default PRange;